// Acasă for the roles that report — the Operator and the Registrar Administrator.
//
// The wireframe gives them ONE page (`wireframes/react/src/pages/Acasa.tsx`), scoped by
// what the role can see, in three parts and in this order:
//
//   1. the day band — have I reported, or have my locations
//   2. Atenție — what is waiting, with a count
//   3. Ultimele rapoarte — the last five, with a way to all of them
//
// The application opened instead with a greeting and a grid of four equal cards, which
// the wireframe has on no role. A greeting says nothing, and four equal cards say
// nothing about which one matters today.
//
// The band branches on how many locations the role holds, exactly as the wireframe
// does (`Acasa.tsx:165` for more than one, `:210` for exactly one): an administrator
// asks "how many of mine have reported", an operator asks "have I".
import { useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { MudBadge, MudButton, MudIcon, MudTable, MudTag } from '@egov-moldova/mud-react';
import { DayStatusBand, formatDateTime, formatTime, onRowOpen, RouterLink } from '@simsm/ui-shared';
import { useDraftsCount } from '../features/reporting/use-drafts-count';
import { useReportingLocations } from '../features/reporting/use-locations';
import { readSavedReport } from '../features/reporting/saved-report';
import { useMyCorrectionCounts } from '../features/corrections/use-my-correction-counts';
import { listSubmissions, type SubmissionSummary } from '../features/reports/api';

/** Today, as the ledger records it: a plain calendar date, timezone-less. */
function today(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** AGE does not export this shape; every register in the application declares it. */
interface TableColumn {
  key: string;
  label: string;
  width?: string;
}

/** The report the band names once one is filed: just saved, or today's latest. */
interface ReportedBand {
  submissionId: string;
  reportNumber: string;
  rowCount: number;
  siteName: string;
  /** The clock of the save, "hh:mm". */
  time: string;
}

export interface ReporterHomeProps {
  /** `/operator` or `/registrar-admin` — the rail this role browses under. */
  rolePrefix: string;
  /**
   * Whether this role oversees locations rather than staffing one.
   *
   * The wireframe branches on `scop.locatii.length > 1` (Acasa.tsx:165), using the
   * count as its proxy for the real question: am I one person at one counter, or do I
   * oversee several? The count is a poor proxy here, because an administrator whose
   * locations are not yet configured has none — and would be asked "have you reported
   * today", which is not their job. So the question is asked directly.
   */
  oversees: boolean;
}

export function ReporterHome({ rolePrefix, oversees }: ReporterHomeProps): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  // Spec 054 US2 (research R8): "Raport nou" lands here after a save with the report
  // in the router state. Read as untrusted input: history state outlives its writer.
  const justSaved = readSavedReport(useLocation().state);
  // The locations this session may report for, from `GET /stock/locations` — the same
  // session-scoped list "Raport nou" and the drafts register read, cached for the
  // session. It used to be `session.locationScope.length`, and that read survived the
  // identity unification which deliberately stopped publishing the field
  // (`dev-identities.ts`: MPass delivers site uuids generated per environment, so no
  // honest value can be checked in). The count was therefore always 0 while
  // `reportedToday` counted real submissions, and the band printed "1 din 0 locații au
  // raportat azi". The name beside it was already read from this list, so the fact had
  // two sources in one function; now it has one.
  const { locations } = useReportingLocations();
  const locationCount = locations.length;
  const { count: draftsCount, drafts } = useDraftsCount(true);
  // FR-026 (contracts/ui-flow.md §Home band): the draft the band continues is
  // the one touched last, and "Continuă raportul" opens THAT draft at its own
  // address (spec 054 T068), not whichever one "Raport nou" would find.
  const openDraft = useMemo(
    () => [...drafts].sort((a, b) => b.updatedAtIso.localeCompare(a.updatedAtIso))[0],
    [drafts],
  );
  // Owner decision D8: the wireframe fills Atentie from the reporter's REJECTED
  // correction requests (Acasa.tsx:100-115). `GET /corrections/mine/counts`
  // answers with all three states, and the sidebar badge reads the same fetch.
  const { counts: correctionCounts } = useMyCorrectionCounts(true);
  const rejectedCorrections = correctionCounts.REJECTED;
  // The wireframe's badge on the section heading counts the ITEMS in the list,
  // not the requests inside one of them (Acasa.tsx:279, `atentie.length`).
  const attentionItems = rejectedCorrections > 0 ? 1 : 0;

  // The last five confirmed reports the role may see, and today's, from the same
  // endpoint the submissions register reads. No new API.
  const recentQuery = useQuery({
    queryKey: ['home-recent-submissions'],
    queryFn: () => listSubmissions({ page: 1, pageSize: 5 }),
    staleTime: 30_000,
  });
  const todayQuery = useQuery({
    queryKey: ['home-today-submissions', today()],
    queryFn: () => listSubmissions({ page: 1, pageSize: 100, from: today(), to: today() }),
    staleTime: 30_000,
  });

  const recent: SubmissionSummary[] = recentQuery.data?.submissions ?? [];
  const reportedSites = useMemo(
    () => new Set((todayQuery.data?.submissions ?? []).map((s) => s.siteId)),
    [todayQuery.data],
  );
  const reportedToday = reportedSites.size;
  const latestToday = useMemo(
    () =>
      (todayQuery.data?.submissions ?? []).reduce<SubmissionSummary | undefined>(
        (latest, s) => (!latest || s.confirmedAtIso > latest.confirmedAtIso ? s : latest),
        undefined,
      ),
    [todayQuery.data],
  );

  const multi = oversees || locationCount > 1;
  const allReported = locationCount > 0 && reportedToday >= locationCount;

  // FR-026, FR-029: once a report is filed the band names it and opens it. The one
  // just saved comes from the router state, on either branch, because the person who
  // saved it asked for exactly this; failing that, the single-location branch names
  // today's latest report (Acasa.tsx:222, "{locație} · N mișcări la ora hh:mm · {id}").
  // The branch that oversees several keeps counting its locations.
  let reported: ReportedBand | null = null;
  if (justSaved) {
    reported = {
      submissionId: justSaved.submissionId,
      reportNumber: justSaved.reportNumber,
      rowCount: justSaved.rowCount,
      siteName: justSaved.siteName,
      time: formatTime(justSaved.savedAtIso ?? new Date().toISOString()),
    };
  } else if (!multi && allReported && latestToday) {
    reported = {
      submissionId: latestToday.id,
      reportNumber: latestToday.reportNumber,
      rowCount: latestToday.rowCount,
      siteName: latestToday.siteName,
      time: formatTime(latestToday.confirmedAtIso),
    };
  }

  const status = reported || allReported ? 'reported' : draftsCount > 0 ? 'draft' : 'not-reported';

  const title = justSaved
    ? t('home.band.saved')
    : multi
      ? allReported
        ? t('home.band.all_reported')
        : reportedToday === 0
          ? t('home.band.none_reported')
          : t('home.band.some_reported', { reported: reportedToday, total: locationCount })
      : allReported
        ? t('home.band.you_reported')
        : draftsCount > 0
          ? t('home.band.you_have_draft')
          : t('home.band.you_not_reported');

  const draftsDetail =
    draftsCount > 0
      ? draftsCount === 1
        ? t('home.band.drafts_detail_one')
        : t('home.band.drafts_detail', { count: draftsCount })
      : undefined;

  // The wireframe's subtitle names the location in every state of the single-location
  // branch (Acasa.tsx:210-217) and names none in the branch above it, whose title
  // already counts them (":170"). So the gate is the resolved list, not the role: a
  // Registrar Administrator holding one site is told which one, and an operator whose
  // MPass scope named nothing is told nothing rather than one site out of five.
  const locationName = locations.length === 1 ? locations[0]?.name : undefined;
  let detail: ReactNode;
  if (reported) {
    detail = t('home.band.saved_detail', {
      location: reported.siteName,
      count: reported.rowCount,
      time: reported.time,
      number: reported.reportNumber,
    });
  } else if (status === 'draft' && !multi && openDraft) {
    // "Ai un raport nefinalizat": "{locație} · N rânduri introduse", and a
    // "{n} cu erori" tag when some rows carry errors (FR-026).
    const draftSite = locations.find((option) => option.id === openDraft.siteId)?.name;
    const withErrors = openDraft.rows.filter((row) => row.validationErrors.length > 0).length;
    const line = [
      draftSite ?? locationName,
      t('home.band.draft_rows', { count: openDraft.rows.length }),
    ]
      .filter((part): part is string => Boolean(part))
      .join(' · ');
    detail = (
      <>
        {line}
        {withErrors > 0 ? (
          <>
            {' '}
            <MudTag
              variant="status"
              size="sm"
              semantic="danger"
              label={t('home.band.draft_errors', { count: withErrors })}
            />
          </>
        ) : null}
      </>
    );
  } else {
    detail =
      [locationName, draftsDetail]
        .filter((part): part is string => part !== undefined)
        .join(' · ') || undefined;
  }

  // The wireframe's two secondary actions are "Toate drafturile" (/nefinalizate) and
  // "Încarcă din Excel" (/import), and it shows exactly one of them, chosen by the
  // state of the day (Acasa.tsx:239-243): the drafts while a report is open, the
  // import while none is. Owner decision D4 built the drafts register and D10 the
  // import screen, so both now lead somewhere and the band follows that rule. The
  // wireframe offers the import on the single-location branch only; it is offered on
  // both here for the same reason the drafts action already is — the alternative way
  // to start the day's report is not a thing one location has and several do not.
  // Four columns, as the wireframe draws them (Acasa.tsx:313-321).
  const columns: TableColumn[] = [
    { key: 'when', label: t('home.recent.column.when') },
    { key: 'site', label: t('home.recent.column.site') },
    { key: 'by', label: t('home.recent.column.by') },
    { key: 'rows', label: t('home.recent.column.rows') },
  ];
  const tableRows = recent.map((row) => ({
    id: row.id,
    when: formatDateTime(row.confirmedAtIso),
    site: row.siteName,
    // The wireframe shows a name. The ledger used to record only an IDNP, which is why
    // this printed one; spec 030 now stores the person's name beside the identifier at
    // the moment of the act, and the submissions register — same endpoint, same object
    // — has been rendering it since. A row written before that change carries no name,
    // and falls back to the identifier rather than to nothing.
    by: row.operatorName || row.operatorIdnp,
    rows: String(row.rowCount),
  }));

  const primaryLabel = allReported
    ? t('home.band.action_new_report')
    : draftsCount > 0
      ? t('home.band.action_continue')
      : t('home.band.action_report_now');
  const primaryPath =
    !allReported && openDraft
      ? `${rolePrefix}/reporting/drafts/${openDraft.id}`
      : `${rolePrefix}/reporting/new`;

  // A filed report: "Vezi raportul" (the wireframe's `outlined strict`, Acasa.tsx:237)
  // opens it, and "Raport nou" is the quiet second action (":243").
  const reportedActions = reported ? (
    <>
      <RouterLink to={`${rolePrefix}/reports/${reported.submissionId}`}>
        <MudButton shape="circular" appearance="outlined" variant="strict" size="md">
          {t('home.band.action_view_report')}
        </MudButton>
      </RouterLink>
      <RouterLink to={`${rolePrefix}/reporting/new`}>
        <MudButton shape="circular" variant="strict" appearance="text" size="md">
          {t('home.band.action_new_report')}
        </MudButton>
      </RouterLink>
    </>
  ) : null;

  return (
    <section className="simsm-home">
      <DayStatusBand
        status={status}
        title={title}
        detail={detail}
        actions={
          reportedActions ?? (
            <>
              <RouterLink to={primaryPath}>
                <MudButton shape="circular" variant="primary" size="md">
                  {primaryLabel}
                </MudButton>
              </RouterLink>
              {/* The wireframe's `btn text strict md`: the quiet second action
                  beside the filled primary, never competing with it. */}
              {draftsCount > 0 ? (
                <RouterLink to={`${rolePrefix}/drafts`}>
                  <MudButton shape="circular" variant="strict" appearance="text" size="md">
                    {t('home.band.action_all_drafts')}
                  </MudButton>
                </RouterLink>
              ) : status === 'not-reported' ? (
                <RouterLink to={`${rolePrefix}/reporting/import`}>
                  <MudButton shape="circular" variant="strict" appearance="text" size="md">
                    {t('home.band.action_import')}
                  </MudButton>
                </RouterLink>
              ) : null}
            </>
          )
        }
      />

      {/* Atenție — only what asks something of THIS reader (the owner's ruling of
          2026-08-24, quoted in Acasa.tsx:96): a request the AMDM rejected is back
          in their hands, while one still awaiting a decision is not. The count is
          the reporter's own, from GET /corrections/mine/counts. */}
      <section>
        <div className="simsm-home__section-head">
          <h2 className="simsm-home__section-title">
            {t('home.section.attention')}
            {attentionItems > 0 ? (
              <MudBadge
                type="numbered"
                variant="default"
                size="sm"
                count={attentionItems}
                aria-label={t('home.attention.badge_aria', { count: attentionItems })}
              />
            ) : null}
          </h2>
        </div>
        {rejectedCorrections > 0 ? (
          <ul className="simsm-home__attention">
            <li className="simsm-home__attention-item">
              <MudTag
                variant="status"
                semantic="accent"
                size="sm"
                label={t('home.attention.tag')}
              />
              <span className="simsm-home__attention-text">
                {rejectedCorrections === 1
                  ? t('home.attention.rejected_one')
                  : t('home.attention.rejected_other', { count: rejectedCorrections })}
              </span>
              {/* The wireframe's `btn text strict sm` carrying a chevron
                  (Acasa.tsx:296-299), not an underlined link: the row is part of a
                  card and its action is a button, the same pair the band uses. */}
              <RouterLink to={`${rolePrefix}/corrections`}>
                <MudButton shape="circular" variant="strict" appearance="text" size="sm">
                  {t('home.attention.see_requests')}
                  <MudIcon slot="icon-end" name="chevron-right" size={16} />
                </MudButton>
              </RouterLink>
            </li>
          </ul>
        ) : (
          <p className="simsm-home__empty">
            <MudIcon name="checkmark-large" size={16} />
            {t('home.section.attention_empty')}
          </p>
        )}
      </section>

      <section>
        <div className="simsm-home__section-head">
          <h2 className="simsm-home__section-title">{t('home.section.recent')}</h2>
          <RouterLink to={`${rolePrefix}/reports`} underline="always">
            {t('home.section.recent_all')}
          </RouterLink>
        </div>
        {recent.length > 0 ? (
          <div className="simsm-home__recent">
            <MudTable
              className="simsm-table"
              columns={columns}
              rows={tableRows}
              rowStyle="zebra"
              disableSort={true}
              aria-label={t('home.section.recent')}
              // The whole row opens the report (Acasa.tsx:324 `tr.clickable`).
              hoverable
              onMudRowClick={onRowOpen((index) => {
                const row = recent[index];
                if (row) navigate(`${rolePrefix}/reports/${row.id}`);
              })}
            />
          </div>
        ) : (
          <p className="simsm-home__empty">
            <MudIcon name="page-text" size={16} />
            {t('home.section.recent_empty')}
          </p>
        )}
      </section>
    </section>
  );
}
